import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Printer, Save, Plus, Trash2, Palette, Box, Tag } from 'lucide-react';
import ProductLabelPreview from './ProductLabelPreview';
import LabelCanvasStudio from './LabelCanvasStudio';
import { LabelCanvasSurface } from '../labelCanvasRenderer';
import { createCanvasElement, nextZIndex } from '../labelCanvasTypes';
import ProductContainerPreview from './ProductContainerPreview';
import ProductStorefrontCardPreview from './ProductStorefrontCardPreview';
import {
  DEFAULT_LABEL_LAYOUT,
  inchesToPx,
  LABEL_PRESET_OPTIONS,
  LABEL_THEME_PRESETS,
  mergeLabelLayout,
  type LabelExtraImage,
  type ProductLabelDesign,
  type ProductLabelLayout,
  type ProductWorkspaceDetail,
} from '../productLabelTypes';
import {
  CONTAINER_TYPE_OPTIONS,
  LABEL_FAMILY_OPTIONS,
  LABEL_PLACEMENT_OPTIONS,
  PACKAGING_PRESENTATION_PRESETS,
  labelFamilyToLayoutTemplate,
  mergeContainerDesign,
  type ProductContainerDesign,
  type ProductContainerType,
  type LabelFamily,
  type LabelPlacement,
} from '../productContainerTypes';
import { LAYOUT_TEMPLATE_OPTIONS, type LabelLayoutTemplate } from '../labelLayoutEngine';
import { exportLabelToPdf } from '../exportLabelPdf';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

type PresetOption = (typeof LABEL_PRESET_OPTIONS)[number] & { key: string };

const PRESET_OPTIONS: PresetOption[] = LABEL_PRESET_OPTIONS.map((opt, idx) => ({
  ...opt,
  key: `${opt.value}-${opt.widthIn}x${opt.heightIn}-${idx}`,
}));

type Props = {
  product: ProductWorkspaceDetail;
  onClose: () => void;
  onSaved?: () => void;
  authorizedFetch: (url: string, init?: RequestInit) => Promise<Response>;
  formatPrice: (n: number) => string;
  currency: string;
};

export default function ProductLabelWorkspace({
  product,
  onClose,
  onSaved,
  authorizedFetch,
  formatPrice,
  currency,
}: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [selectedPresetKey, setSelectedPresetKey] = useState(PRESET_OPTIONS[2].key);
  const [workspaceTab, setWorkspaceTab] = useState<'container' | 'label' | 'sellable'>('container');
  const [previewMode, setPreviewMode] = useState<'studio' | 'mockup' | 'storefront'>('studio');
  const [exportingPdf, setExportingPdf] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  type PackagingRow = {
    id?: string;
    label: string;
    sizeLbs: string;
    isDefault: boolean;
    packageMaterial: string;
    packageWeightLbs: string;
    packageLengthIn: string;
    packageWidthIn: string;
    packageHeightIn: string;
    presentation: ProductContainerDesign;
  };
  const [packagingRows, setPackagingRows] = useState<PackagingRow[]>([]);
  const [design, setDesign] = useState<ProductLabelDesign>({
    id: null,
    productId: product.id,
    preset: 'SHELF_4X2',
    widthIn: 4,
    heightIn: 2,
    layout: DEFAULT_LABEL_LAYOUT,
    packageWeightLbs: null,
    packageLengthIn: null,
    packageWidthIn: null,
    packageHeightIn: null,
    packageMaterial: null,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [labelRes, pkgRes] = await Promise.all([
        authorizedFetch(`${API_BASE}/catalog/products/${product.id}/label-design`),
        authorizedFetch(`${API_BASE}/catalog/products/${product.id}/packaging/workspace`),
      ]);
      if (!labelRes.ok) throw new Error('load');
      const data = (await labelRes.json()) as ProductLabelDesign;
      setDesign({
        ...data,
        layout: mergeLabelLayout(data.layout as Partial<ProductLabelLayout>),
      });
      const matchPreset = PRESET_OPTIONS.find(
        (o) =>
          o.value === data.preset &&
          o.widthIn === data.widthIn &&
          o.heightIn === data.heightIn,
      );
      if (matchPreset) setSelectedPresetKey(matchPreset.key);
      else {
        const custom = PRESET_OPTIONS.find((o) => o.label === 'Custom size');
        if (custom) setSelectedPresetKey(custom.key);
      }
      if (pkgRes.ok) {
        const pkg = (await pkgRes.json()) as Array<{
          id: string;
          label: string;
          sizeLbs: number;
          isDefault: boolean;
          packageMaterial?: string | null;
          packageWeightLbs?: number | null;
          packageLengthIn?: number | null;
          packageWidthIn?: number | null;
          packageHeightIn?: number | null;
          presentation?: Partial<ProductContainerDesign> | null;
        }>;
        const loadedLayout = mergeLabelLayout(data.layout as Partial<ProductLabelLayout>);
        setPackagingRows(
          pkg.length > 0
            ? pkg.map((row) => ({
                id: row.id,
                label: row.label,
                sizeLbs: String(row.sizeLbs),
                isDefault: row.isDefault,
                packageMaterial: row.packageMaterial || '',
                packageWeightLbs:
                  row.packageWeightLbs != null ? String(row.packageWeightLbs) : '',
                packageLengthIn:
                  row.packageLengthIn != null ? String(row.packageLengthIn) : '',
                packageWidthIn:
                  row.packageWidthIn != null ? String(row.packageWidthIn) : '',
                packageHeightIn:
                  row.packageHeightIn != null ? String(row.packageHeightIn) : '',
                presentation: mergeContainerDesign({
                  ...loadedLayout.containerDesign,
                  ...(row.presentation as Partial<ProductContainerDesign> | undefined),
                }),
              }))
            : [],
        );
      }
    } catch {
      setError('Unable to load label design');
    } finally {
      setLoading(false);
    }
  }, [authorizedFetch, product.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const updateLayout = (patch: Partial<ProductLabelLayout>) => {
    setDesign((prev) => ({
      ...prev,
      layout: { ...mergeLabelLayout(prev.layout), ...patch },
    }));
  };

  const updateContainerDesign = (patch: Partial<ProductContainerDesign>) => {
    updateLayout({
      containerDesign: mergeContainerDesign({
        ...mergeLabelLayout(design.layout).containerDesign,
        ...patch,
      }),
    });
    setPackagingRows((rows) =>
      rows.map((row) =>
        row.isDefault
          ? { ...row, presentation: mergeContainerDesign({ ...row.presentation, ...patch }) }
          : row,
      ),
    );
  };

  const applyPresentationPreset = (presetId: string) => {
    const preset = PACKAGING_PRESENTATION_PRESETS.find((p) => p.id === presetId);
    if (!preset) return;
    updateLayout({
      layoutTemplate: preset.layoutTemplate,
      containerDesign: preset.container,
    });
    setDesign((prev) => ({
      ...prev,
      preset: 'CUSTOM',
      widthIn: preset.widthIn,
      heightIn: preset.heightIn,
    }));
    setPackagingRows((rows) =>
      rows.map((row) =>
        row.isDefault ? { ...row, presentation: { ...preset.container } } : row,
      ),
    );
  };

  const applyTheme = (themeId: string) => {
    const theme = LABEL_THEME_PRESETS.find((t) => t.id === themeId);
    if (!theme) return;
    updateLayout(theme.layout);
  };

  const selectPreset = (opt: PresetOption) => {
    setSelectedPresetKey(opt.key);
    setDesign((prev) => ({
      ...prev,
      preset: opt.value,
      widthIn: opt.widthIn,
      heightIn: opt.heightIn,
    }));
  };

  const uploadExtraImage = async (file: File, slotIndex: number) => {
    setImageUploading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await authorizedFetch(`${API_BASE}/catalog/products/image`, {
        method: 'POST',
        body: formData,
      });
      if (!res.ok) throw new Error('upload');
      const body = (await res.json()) as { url?: string };
      const url = body.url || '';
      updateLayout({
        extraImages: mergeLabelLayout(design.layout).extraImages.map((img, i) =>
          i === slotIndex ? { ...img, url } : img,
        ),
      });
    } catch {
      setError('Unable to upload image');
    } finally {
      setImageUploading(false);
    }
  };

  const addExtraImageSlot = () => {
    const current = mergeLabelLayout(design.layout).extraImages;
    if (current.length >= 4) return;
    updateLayout({
      extraImages: [
        ...current,
        { id: `extra-${Date.now()}`, url: '', label: `Image ${current.length + 1}` },
      ],
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await authorizedFetch(`${API_BASE}/catalog/products/${product.id}/label-design`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          preset: design.preset,
          widthIn: design.widthIn,
          heightIn: design.heightIn,
          layout: design.layout,
          packageWeightLbs: design.packageWeightLbs,
          packageLengthIn: design.packageLengthIn,
          packageWidthIn: design.packageWidthIn,
          packageHeightIn: design.packageHeightIn,
          packageMaterial: design.packageMaterial,
        }),
      });
      if (!res.ok) throw new Error('save');
      const saved = (await res.json()) as ProductLabelDesign;
      setDesign({
        ...saved,
        layout: mergeLabelLayout(saved.layout as Partial<ProductLabelLayout>),
      });
      const pkgRes = await authorizedFetch(`${API_BASE}/catalog/products/${product.id}/packaging`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          options: packagingRows
            .filter((row) => row.label.trim() && Number(row.sizeLbs) > 0)
            .map((row, idx) => ({
              id: row.id,
              label: row.label.trim(),
              sizeLbs: Number(row.sizeLbs),
              sortOrder: idx,
              isDefault: row.isDefault,
              packageMaterial: row.packageMaterial.trim() || null,
              packageWeightLbs:
                row.packageWeightLbs === '' ? null : Number(row.packageWeightLbs),
              packageLengthIn:
                row.packageLengthIn === '' ? null : Number(row.packageLengthIn),
              packageWidthIn:
                row.packageWidthIn === '' ? null : Number(row.packageWidthIn),
              packageHeightIn:
                row.packageHeightIn === '' ? null : Number(row.packageHeightIn),
              presentation: row.presentation,
            })),
        }),
      });
      if (!pkgRes.ok) throw new Error('packaging save');
      setSuccess('Label and packaging saved');
      onSaved?.();
    } catch {
      setError('Unable to save label design');
    } finally {
      setSaving(false);
    }
  };

  const downloadLabelPdf = async () => {
    const el = document.getElementById('product-label-print-root');
    if (!el) {
      setError('Label preview not ready');
      return;
    }
    setExportingPdf(true);
    setError(null);
    try {
      const safeName = product.name.replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'product';
      await exportLabelToPdf(el, design.widthIn, design.heightIn, `${safeName}-label.pdf`);
    } catch {
      setError('Unable to generate PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  const layout = mergeLabelLayout(design.layout);
  const activeContainer = useMemo(() => {
    const def = packagingRows.find((r) => r.isDefault) || packagingRows[0];
    if (def) return mergeContainerDesign(def.presentation);
    return layout.containerDesign;
  }, [layout.containerDesign, packagingRows]);

  const previewDesign = useMemo(() => {
    const def = packagingRows.find((r) => r.isDefault) || packagingRows[0];
    if (!def) return design;
    return {
      ...design,
      packageMaterial: def.packageMaterial || design.packageMaterial,
      packageWeightLbs:
        def.packageWeightLbs === '' ? design.packageWeightLbs : Number(def.packageWeightLbs),
      packageLengthIn:
        def.packageLengthIn === '' ? design.packageLengthIn : Number(def.packageLengthIn),
      packageWidthIn:
        def.packageWidthIn === '' ? design.packageWidthIn : Number(def.packageWidthIn),
      packageHeightIn:
        def.packageHeightIn === '' ? design.packageHeightIn : Number(def.packageHeightIn),
    };
  }, [design, packagingRows]);

  const netQuantityLabel = useMemo(() => {
    const def = packagingRows.find((r) => r.isDefault) || packagingRows[0];
    if (!def) return null;
    if (def.label.trim()) return def.label.trim().toUpperCase();
    const lbs = Number(def.sizeLbs);
    if (lbs > 0) return `${lbs} LB`;
    return null;
  }, [packagingRows]);

  const toggleFields: Array<{ key: keyof ProductLabelLayout; label: string }> = [
    { key: 'showBrand', label: 'Brand panel' },
    { key: 'showImage', label: 'Product image' },
    { key: 'showName', label: 'Product name' },
    { key: 'showPrice', label: 'Sale price' },
    { key: 'showNetQuantity', label: 'Net quantity badge' },
    { key: 'showCategory', label: 'Category' },
    { key: 'showDescription', label: 'Description' },
    { key: 'showBenefits', label: 'Benefits' },
    { key: 'showHsCode', label: 'HS code' },
    { key: 'showCountryOfOrigin', label: 'Country of origin' },
    { key: 'showExpiry', label: 'Shelf life' },
    { key: 'showPackaging', label: 'Packaging details' },
    { key: 'showCompanyInfo', label: 'Company block' },
    { key: 'showMarketing', label: 'Marketing panel' },
    { key: 'showSku', label: 'SKU / ID' },
    { key: 'showBarcode', label: 'Barcode' },
    { key: 'showUnit', label: 'Unit of sale' },
  ];

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-slate-100">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-white px-4 py-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">
            {t('backoffice.products_label_workspace', { defaultValue: 'Label workspace' })}
          </h2>
          <p className="text-xs text-slate-600">
            {product.name} · {product.sku}
          </p>
          <p className="mt-0.5 text-[10px] text-slate-500">
            Design the physical container and label artwork — separate from workspace{' '}
            <span className="font-medium">Packages</span> (product bundles).
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={saving}
            onClick={() => void save()}
            className="inline-flex items-center gap-1 border border-slate-800 bg-slate-800 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
          >
            <Save className="h-3.5 w-3.5" />
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            onClick={() => void downloadLabelPdf()}
            disabled={exportingPdf}
            className="inline-flex items-center gap-1 border border-sky-700 bg-sky-700 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
          >
            <Printer className="h-3.5 w-3.5" />
            {exportingPdf ? 'PDF…' : 'Download PDF'}
          </button>
          <button type="button" onClick={onClose} className="border border-slate-300 p-1.5">
            <X className="h-4 w-4" />
          </button>
        </div>
      </header>
      {error ? <p className="bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p> : null}
      {success ? <p className="bg-emerald-50 px-4 py-2 text-sm text-emerald-700">{success}</p> : null}
      {loading ? (
        <div className="flex flex-1 items-center justify-center text-sm text-slate-600">Loading…</div>
      ) : (
        <div
          className={`grid flex-1 gap-0 overflow-hidden ${
            workspaceTab === 'label' ? 'lg:grid-cols-[minmax(15rem,18rem)_1fr]' : 'lg:grid-cols-2'
          }`}
        >
          <section className="flex flex-col overflow-hidden border-r border-slate-200 bg-white">
            <div className="flex shrink-0 border-b border-slate-200">
              {(
                [
                  { id: 'container' as const, label: 'Container', icon: Box },
                  { id: 'label' as const, label: 'Label artwork', icon: Tag },
                  { id: 'sellable' as const, label: 'Sellable units', icon: Plus },
                ] as const
              ).map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setWorkspaceTab(tab.id)}
                  className={`flex flex-1 items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-semibold ${
                    workspaceTab === tab.id
                      ? 'border-b-2 border-sky-600 text-sky-800'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <tab.icon className="h-3.5 w-3.5" />
                  {tab.label}
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-y-auto p-4">
            {workspaceTab === 'container' ? (
              <>
            <h3 className="mb-2 text-sm font-semibold text-slate-900">Presentation presets</h3>
            <p className="mb-3 text-[11px] text-slate-600">
              Real-world packaging families: clear bags, pouches, jars, bottles, nutrition panels.
            </p>
            <div className="mb-5 grid gap-2 sm:grid-cols-2">
              {PACKAGING_PRESENTATION_PRESETS.map((preset) => (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => applyPresentationPreset(preset.id)}
                  className="border border-slate-200 px-2 py-2 text-left text-xs hover:border-sky-500"
                >
                  <span className="font-semibold text-slate-900">{preset.name}</span>
                  <span className="mt-0.5 block text-[10px] text-slate-500">{preset.description}</span>
                </button>
              ))}
            </div>
            <h3 className="mb-2 text-sm font-semibold text-slate-900">Container type</h3>
            <div className="mb-4 grid gap-2 sm:grid-cols-2">
              {CONTAINER_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => updateContainerDesign({ containerType: opt.value })}
                  className={`border px-2 py-2 text-left text-xs ${
                    activeContainer.containerType === opt.value
                      ? 'border-sky-600 bg-sky-50'
                      : 'border-slate-200'
                  }`}
                >
                  <span className="font-semibold">{opt.name}</span>
                  <span className="mt-0.5 block text-[10px] text-slate-500">{opt.description}</span>
                </button>
              ))}
            </div>
            <div className="mb-4 grid grid-cols-2 gap-2">
              <label className="text-xs">
                Label placement
                <select
                  className="mt-1 w-full border border-slate-300 px-2 py-1.5"
                  value={activeContainer.labelPlacement}
                  onChange={(e) =>
                    updateContainerDesign({ labelPlacement: e.target.value as LabelPlacement })
                  }
                >
                  {LABEL_PLACEMENT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs">
                Label family
                <select
                  className="mt-1 w-full border border-slate-300 px-2 py-1.5"
                  value={activeContainer.labelFamily}
                  onChange={(e) => {
                    const family = e.target.value as LabelFamily;
                    updateContainerDesign({ labelFamily: family });
                    updateLayout({ layoutTemplate: labelFamilyToLayoutTemplate(family) });
                  }}
                >
                  {LABEL_FAMILY_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="text-xs">
                Container color
                <input
                  type="color"
                  className="mt-1 h-9 w-full border border-slate-300"
                  value={activeContainer.containerColor}
                  onChange={(e) => updateContainerDesign({ containerColor: e.target.value })}
                />
              </label>
              <label className="text-xs">
                Accent / trim
                <input
                  type="color"
                  className="mt-1 h-9 w-full border border-slate-300"
                  value={activeContainer.containerAccentColor}
                  onChange={(e) => updateContainerDesign({ containerAccentColor: e.target.value })}
                />
              </label>
              <label className="text-xs">
                Lid / cap color
                <input
                  type="color"
                  className="mt-1 h-9 w-full border border-slate-300"
                  value={activeContainer.lidColor}
                  onChange={(e) => updateContainerDesign({ lidColor: e.target.value })}
                />
              </label>
              <label className="text-xs">
                Fill visibility
                <input
                  type="range"
                  min="0.1"
                  max="1"
                  step="0.05"
                  className="mt-2 w-full"
                  value={activeContainer.fillOpacity}
                  onChange={(e) =>
                    updateContainerDesign({ fillOpacity: Number(e.target.value) })
                  }
                />
              </label>
            </div>
              </>
            ) : null}

            {workspaceTab === 'label' ? (
              <>
            <h3 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-900">
              <Palette className="h-4 w-4" />
              Color themes
            </h3>
            <div className="mb-4 grid gap-2 sm:grid-cols-2">
              {LABEL_THEME_PRESETS.map((theme) => (
                <button
                  key={theme.id}
                  type="button"
                  onClick={() => applyTheme(theme.id)}
                  className="border border-slate-200 px-2 py-2 text-left text-xs hover:border-sky-400"
                >
                  <span className="font-semibold text-slate-900">{theme.name}</span>
                  <span className="mt-0.5 block text-[10px] text-slate-500">{theme.description}</span>
                </button>
              ))}
            </div>

            <h3 className="mb-3 text-sm font-semibold text-slate-900">Label size</h3>
            <div className="mb-4 grid gap-2 sm:grid-cols-2">
              {PRESET_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => selectPreset(opt)}
                  className={`border px-2 py-2 text-left text-xs ${
                    selectedPresetKey === opt.key
                      ? 'border-sky-600 bg-sky-50 text-sky-900'
                      : 'border-slate-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {design.preset === 'CUSTOM' ? (
              <div className="mb-4 grid grid-cols-2 gap-2">
                <label className="text-xs">
                  Width (in)
                  <input
                    type="number"
                    step="0.1"
                    className="mt-1 w-full border border-slate-300 px-2 py-1.5"
                    value={design.widthIn}
                    onChange={(e) => setDesign((p) => ({ ...p, widthIn: Number(e.target.value) || 1 }))}
                  />
                </label>
                <label className="text-xs">
                  Height (in)
                  <input
                    type="number"
                    step="0.1"
                    className="mt-1 w-full border border-slate-300 px-2 py-1.5"
                    value={design.heightIn}
                    onChange={(e) => setDesign((p) => ({ ...p, heightIn: Number(e.target.value) || 1 }))}
                  />
                </label>
              </div>
            ) : null}

            <h3 className="mb-2 text-sm font-semibold text-slate-900">Design studio</h3>
            <p className="mb-2 text-[11px] text-slate-600">
              Use the canvas on the right to place text, shapes, images, and product data anywhere —
              like a professional packaging label. Pick a starter design or build from scratch.
            </p>
            <button
              type="button"
              onClick={() =>
                updateLayout({
                  layoutTemplate: 'CANVAS',
                  canvasElements:
                    layout.canvasElements.length > 0
                      ? layout.canvasElements
                      : [
                          createCanvasElement('shape', {
                            x: 0,
                            y: 0,
                            w: 100,
                            h: 100,
                            zIndex: 0,
                            backgroundColor: layout.backgroundColor,
                          }),
                          createCanvasElement('boundField', {
                            fieldKey: 'name',
                            x: 8,
                            y: 12,
                            w: 55,
                            h: 18,
                            zIndex: nextZIndex([]),
                            fontSizePct: 5,
                            fontWeight: 700,
                            color: layout.accentColor,
                          }),
                          createCanvasElement('boundField', {
                            fieldKey: 'image',
                            x: 62,
                            y: 15,
                            w: 30,
                            h: 55,
                            zIndex: 2,
                          }),
                          createCanvasElement('barcode', {
                            x: 8,
                            y: 72,
                            w: 35,
                            h: 18,
                            zIndex: 3,
                          }),
                        ],
                })
              }
              className="mb-3 w-full border border-sky-600 bg-sky-50 px-2 py-2 text-xs font-semibold text-sky-900 hover:bg-sky-100"
            >
              Open design studio
            </button>
            <label className="mb-3 flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={layout.autoFit}
                onChange={(e) => updateLayout({ autoFit: e.target.checked })}
              />
              Auto-fit content to label size
            </label>
            <div className="mb-4 grid gap-2 sm:grid-cols-2">
              {LAYOUT_TEMPLATE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => updateLayout({ layoutTemplate: opt.value as LabelLayoutTemplate })}
                  className={`border px-2 py-2 text-left text-xs ${
                    layout.layoutTemplate === opt.value
                      ? 'border-sky-600 bg-sky-50'
                      : 'border-slate-200'
                  }`}
                >
                  <span className="font-semibold">{opt.name}</span>
                  <span className="mt-0.5 block text-[10px] text-slate-500">{opt.description}</span>
                </button>
              ))}
            </div>

            <h3 className="mb-2 text-sm font-semibold text-slate-900">Extra label images</h3>
            <p className="mb-2 text-[11px] text-slate-600">
              Add certification badges, logos, or icons alongside the product photo.
            </p>
            <div className="mb-4 space-y-2">
              {layout.extraImages.map((img: LabelExtraImage, idx: number) => (
                <div key={img.id} className="flex flex-wrap items-end gap-2 border border-slate-200 p-2">
                  <label className="min-w-0 flex-1 text-xs">
                    Caption
                    <input
                      className="mt-1 w-full border border-slate-300 px-2 py-1.5"
                      value={img.label || ''}
                      onChange={(e) =>
                        updateLayout({
                          extraImages: layout.extraImages.map((row, i) =>
                            i === idx ? { ...row, label: e.target.value } : row,
                          ),
                        })
                      }
                    />
                  </label>
                  <label className="min-w-0 flex-1 text-xs">
                    Image URL
                    <input
                      className="mt-1 w-full border border-slate-300 px-2 py-1.5"
                      value={img.url}
                      placeholder="https://…"
                      onChange={(e) =>
                        updateLayout({
                          extraImages: layout.extraImages.map((row, i) =>
                            i === idx ? { ...row, url: e.target.value } : row,
                          ),
                        })
                      }
                    />
                  </label>
                  <label className="cursor-pointer border border-slate-300 px-2 py-1.5 text-xs">
                    {imageUploading ? 'Uploading…' : 'Upload'}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      disabled={imageUploading}
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) void uploadExtraImage(f, idx);
                        e.target.value = '';
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    className="text-rose-700"
                    onClick={() =>
                      updateLayout({
                        extraImages: layout.extraImages.filter((_, i) => i !== idx),
                      })
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                disabled={layout.extraImages.length >= 4}
                onClick={addExtraImageSlot}
                className="inline-flex items-center gap-1 border border-slate-300 px-2 py-1.5 text-xs disabled:opacity-50"
              >
                <Plus className="h-3.5 w-3.5" />
                Add image slot
              </button>
            </div>

            <h3 className="mb-2 text-sm font-semibold text-slate-900">Visible sections</h3>
            <div className="mb-4 grid grid-cols-2 gap-2">
              {toggleFields.map((f) => (
                <label key={f.key} className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={Boolean(layout[f.key])}
                    onChange={(e) => updateLayout({ [f.key]: e.target.checked } as Partial<ProductLabelLayout>)}
                  />
                  {f.label}
                </label>
              ))}
            </div>

            <h3 className="mb-2 text-sm font-semibold text-slate-900">Brand &amp; marketing</h3>
            <div className="mb-4 grid grid-cols-2 gap-2">
              <label className="text-xs sm:col-span-2">
                Brand name
                <input
                  className="mt-1 w-full border border-slate-300 px-2 py-1.5"
                  value={layout.brandName}
                  onChange={(e) => updateLayout({ brandName: e.target.value })}
                />
              </label>
              <label className="text-xs sm:col-span-2">
                Marketing tagline
                <input
                  className="mt-1 w-full border border-slate-300 px-2 py-1.5"
                  placeholder="Share freshness with"
                  value={layout.marketingTagline}
                  onChange={(e) => updateLayout({ marketingTagline: e.target.value })}
                />
              </label>
              <label className="text-xs">
                Hashtag
                <input
                  className="mt-1 w-full border border-slate-300 px-2 py-1.5"
                  placeholder="#Lewouhmart"
                  value={layout.marketingHashtag}
                  onChange={(e) => updateLayout({ marketingHashtag: e.target.value })}
                />
              </label>
              <label className="text-xs">
                Footer text
                <input
                  className="mt-1 w-full border border-slate-300 px-2 py-1.5"
                  value={layout.footerText}
                  onChange={(e) => updateLayout({ footerText: e.target.value })}
                />
              </label>
            </div>

            <h3 className="mb-2 text-sm font-semibold text-slate-900">Company &amp; legal</h3>
            <div className="mb-4 grid grid-cols-2 gap-2">
              <label className="text-xs">
                Phone
                <input
                  className="mt-1 w-full border border-slate-300 px-2 py-1.5"
                  value={layout.companyPhone}
                  onChange={(e) => updateLayout({ companyPhone: e.target.value })}
                />
              </label>
              <label className="text-xs">
                Website
                <input
                  className="mt-1 w-full border border-slate-300 px-2 py-1.5"
                  value={layout.companyWebsite}
                  onChange={(e) => updateLayout({ companyWebsite: e.target.value })}
                />
              </label>
              <label className="text-xs sm:col-span-2">
                Legal / handling line
                <input
                  className="mt-1 w-full border border-slate-300 px-2 py-1.5"
                  value={layout.companyLegalLine}
                  onChange={(e) => updateLayout({ companyLegalLine: e.target.value })}
                />
              </label>
            </div>

            <h3 className="mb-2 text-sm font-semibold text-slate-900">Colors &amp; typography</h3>
            <div className="mb-4 grid grid-cols-2 gap-2">
              <label className="text-xs">
                Orientation
                <select
                  className="mt-1 w-full border border-slate-300 px-2 py-1.5"
                  value={layout.orientation}
                  onChange={(e) =>
                    updateLayout({ orientation: e.target.value as 'landscape' | 'portrait' })
                  }
                >
                  <option value="landscape">Landscape</option>
                  <option value="portrait">Portrait</option>
                </select>
              </label>
              <label className="text-xs">
                Font scale
                <input
                  type="range"
                  min="0.75"
                  max="1.5"
                  step="0.05"
                  className="mt-2 w-full"
                  value={layout.fontScale}
                  onChange={(e) => updateLayout({ fontScale: Number(e.target.value) })}
                />
              </label>
              <label className="text-xs">
                Main background
                <input
                  type="color"
                  className="mt-1 h-9 w-full border border-slate-300"
                  value={layout.backgroundColor}
                  onChange={(e) => updateLayout({ backgroundColor: e.target.value })}
                />
              </label>
              <label className="text-xs">
                Main text
                <input
                  type="color"
                  className="mt-1 h-9 w-full border border-slate-300"
                  value={layout.textColor}
                  onChange={(e) => updateLayout({ textColor: e.target.value })}
                />
              </label>
              <label className="text-xs">
                Accent
                <input
                  type="color"
                  className="mt-1 h-9 w-full border border-slate-300"
                  value={layout.accentColor}
                  onChange={(e) => updateLayout({ accentColor: e.target.value })}
                />
              </label>
              <label className="text-xs">
                Legal panel bg
                <input
                  type="color"
                  className="mt-1 h-9 w-full border border-slate-300"
                  value={layout.secondaryBackgroundColor}
                  onChange={(e) => updateLayout({ secondaryBackgroundColor: e.target.value })}
                />
              </label>
            </div>

              </>
            ) : null}

            {workspaceTab === 'sellable' ? (
              <>
            <h3 className="mb-2 text-sm font-semibold text-slate-900">Sellable units</h3>
            <p className="mb-2 text-[11px] text-slate-600">
              Each row is a customer-facing unit (e.g. 2 lb bag). This is the physical pack size, not
              workspace Packages bundles.
            </p>
            <div className="space-y-3">
              {packagingRows.map((row, idx) => (
                <div key={row.id || `new-${idx}`} className="border border-slate-200 p-2">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <label className="flex items-center gap-2 text-xs">
                      <input
                        type="radio"
                        name={`default-pack-${product.id}`}
                        checked={row.isDefault}
                        onChange={() =>
                          setPackagingRows((rows) =>
                            rows.map((r, i) => ({ ...r, isDefault: i === idx })),
                          )
                        }
                      />
                      Default on label
                    </label>
                    <button
                      type="button"
                      onClick={() =>
                        setPackagingRows((rows) => rows.filter((_, i) => i !== idx))
                      }
                      className="text-rose-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="text-xs sm:col-span-2">
                      Label (shown to customer)
                      <input
                        className="mt-1 w-full border border-slate-300 px-2 py-1.5"
                        placeholder="2 lb bag"
                        value={row.label}
                        onChange={(e) =>
                          setPackagingRows((rows) =>
                            rows.map((r, i) =>
                              i === idx ? { ...r, label: e.target.value } : r,
                            ),
                          )
                        }
                      />
                    </label>
                    <label className="text-xs">
                      Net size (lb)
                      <input
                        type="number"
                        step="0.1"
                        className="mt-1 w-full border border-slate-300 px-2 py-1.5"
                        value={row.sizeLbs}
                        onChange={(e) =>
                          setPackagingRows((rows) =>
                            rows.map((r, i) =>
                              i === idx ? { ...r, sizeLbs: e.target.value } : r,
                            ),
                          )
                        }
                      />
                    </label>
                    <label className="text-xs">
                      Ship weight (lb)
                      <input
                        type="number"
                        step="0.01"
                        className="mt-1 w-full border border-slate-300 px-2 py-1.5"
                        value={row.packageWeightLbs}
                        onChange={(e) =>
                          setPackagingRows((rows) =>
                            rows.map((r, i) =>
                              i === idx ? { ...r, packageWeightLbs: e.target.value } : r,
                            ),
                          )
                        }
                      />
                    </label>
                    <label className="text-xs sm:col-span-2">
                      Container for this unit
                      <select
                        className="mt-1 w-full border border-slate-300 px-2 py-1.5"
                        value={row.presentation.containerType}
                        onChange={(e) =>
                          setPackagingRows((rows) =>
                            rows.map((r, i) =>
                              i === idx
                                ? {
                                    ...r,
                                    presentation: {
                                      ...r.presentation,
                                      containerType: e.target.value as ProductContainerType,
                                    },
                                  }
                                : r,
                            ),
                          )
                        }
                      >
                        {CONTAINER_TYPE_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.name}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  setPackagingRows((rows) => [
                    ...rows,
                    {
                      label: '',
                      sizeLbs: '2',
                      isDefault: rows.length === 0,
                      packageMaterial: '',
                      packageWeightLbs: '',
                      packageLengthIn: '',
                      packageWidthIn: '',
                      packageHeightIn: '',
                      presentation: mergeContainerDesign(layout.containerDesign),
                    },
                  ])
                }
                className="inline-flex items-center gap-1 border border-slate-300 px-2 py-1.5 text-xs"
              >
                <Plus className="h-3.5 w-3.5" />
                Add sellable unit
              </button>
            </div>
              </>
            ) : null}
            </div>
          </section>
          <section
            className={`flex flex-col overflow-hidden ${
              workspaceTab === 'label' && previewMode === 'studio'
                ? 'bg-slate-100'
                : 'overflow-auto bg-gradient-to-br from-slate-200/80 to-slate-300/60 p-4 sm:p-6'
            }`}
          >
            <div className="mb-3 flex shrink-0 flex-wrap justify-center gap-2 p-2 sm:p-0 sm:pt-4">
              {(
                workspaceTab === 'label'
                  ? [
                      { id: 'studio' as const, label: 'Design studio' },
                      { id: 'mockup' as const, label: 'On container' },
                      { id: 'storefront' as const, label: 'Storefront card' },
                    ]
                  : [
                      { id: 'mockup' as const, label: 'On container' },
                      { id: 'storefront' as const, label: 'Storefront card' },
                    ]
              ).map((mode) => (
                <button
                  key={mode.id}
                  type="button"
                  onClick={() => setPreviewMode(mode.id)}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
                    previewMode === mode.id ? 'bg-white text-sky-800 shadow' : 'text-slate-600'
                  }`}
                >
                  {mode.label}
                </button>
              ))}
            </div>
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center">
              {previewMode === 'storefront' ? (
                <ProductStorefrontCardPreview
                  product={product}
                  container={activeContainer}
                  design={previewDesign}
                  netQuantityLabel={netQuantityLabel}
                  formatPrice={formatPrice}
                  currency={currency}
                />
              ) : workspaceTab === 'label' && previewMode === 'studio' ? (
                <LabelCanvasStudio
                  product={product}
                  design={previewDesign}
                  netQuantityLabel={netQuantityLabel}
                  formatPrice={formatPrice}
                  currency={currency}
                  printId="product-label-print-root"
                  onLayoutChange={updateLayout}
                  onDesignChange={(patch) => setDesign((p) => ({ ...p, ...patch }))}
                  onUploadImage={async (file) => {
                    setImageUploading(true);
                    try {
                      const formData = new FormData();
                      formData.append('file', file);
                      const res = await authorizedFetch(`${API_BASE}/catalog/products/image`, {
                        method: 'POST',
                        body: formData,
                      });
                      if (!res.ok) return null;
                      const body = (await res.json()) as { url?: string };
                      return body.url || null;
                    } catch {
                      return null;
                    } finally {
                      setImageUploading(false);
                    }
                  }}
                />
              ) : workspaceTab === 'label' && previewMode === 'mockup' ? (
                <ProductContainerPreview
                  container={activeContainer}
                  productImageUrl={product.imageUrl}
                  className="mb-2"
                >
                  {mergeLabelLayout(previewDesign.layout).layoutTemplate === 'CANVAS' ? (
                    <LabelCanvasSurface
                      ctx={{
                        product,
                        layout: mergeLabelLayout(previewDesign.layout),
                        design: previewDesign,
                        netQuantityLabel,
                        formatPrice,
                        currency,
                        labelW: inchesToPx(previewDesign.widthIn),
                        labelH: inchesToPx(previewDesign.heightIn),
                      }}
                      printId="product-label-print-root"
                    />
                  ) : (
                    <ProductLabelPreview
                      product={product}
                      design={previewDesign}
                      netQuantityLabel={netQuantityLabel}
                      formatPrice={formatPrice}
                      currency={currency}
                      printId="product-label-print-root"
                    />
                  )}
                </ProductContainerPreview>
              ) : previewMode === 'mockup' ? (
                <ProductContainerPreview
                  container={activeContainer}
                  productImageUrl={product.imageUrl}
                  className="mb-2"
                >
                  <ProductLabelPreview
                    product={product}
                    design={previewDesign}
                    netQuantityLabel={netQuantityLabel}
                    formatPrice={formatPrice}
                    currency={currency}
                    printId="product-label-print-root"
                  />
                </ProductContainerPreview>
              ) : (
                <div className="rounded-lg bg-white/50 p-4 shadow-inner">
                  <ProductLabelPreview
                    product={product}
                    design={previewDesign}
                    netQuantityLabel={netQuantityLabel}
                    formatPrice={formatPrice}
                    currency={currency}
                  />
                </div>
              )}
            </div>
            <p className="mt-3 shrink-0 text-center text-[11px] text-slate-600">
              {CONTAINER_TYPE_OPTIONS.find((c) => c.value === activeContainer.containerType)?.name} ·{' '}
              {LABEL_FAMILY_OPTIONS.find((l) => l.value === activeContainer.labelFamily)?.name} ·{' '}
              {design.widthIn}″ × {design.heightIn}″ label
            </p>
          </section>
        </div>
      )}
    </div>
  );
}
